import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { prettierBytes } from '@transloadit/prettier-bytes';
import classNames from 'classnames';
import { useEffect, useId, useState } from 'preact/hooks';
import { getApplicableActions } from './Item/components/ItemActionsMenu.js';
import ItemIcon from './Item/components/ItemIcon.js';
import ModalDialog from './ModalDialog.js';
/**
 * The detail view of one item in manager mode: a {@link ModalDialog} with a
 * preview, the item's metadata, and the same actions the "…" menu offers (they
 * are the one shared list).
 */
export default function ItemDetailDialog({ item, actions, runAction, getPreviewUrl, onClose, i18n, }) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const titleId = useId();
    // The item as the dialog opened: a listing update replaces the `item` object
    // but not the preview (another item gets a new dialog; see its `key`).
    const [previewItem] = useState(item);
    useEffect(() => {
        if (!getPreviewUrl || previewItem.data.isFolder)
            return;
        let cancelled = false;
        // Inside `then`: a JavaScript integrator may throw synchronously or return
        // any thenable.
        Promise.resolve()
            .then(() => getPreviewUrl(previewItem))
            .then((url) => {
            if (!cancelled)
                setPreviewUrl(url);
        })
            .catch(() => {
            /* previews are a nicety: fall back to the icon */
        });
        return () => {
            cancelled = true;
        };
    }, [getPreviewUrl, previewItem]);
    const name = item.data.name ?? i18n('unnamed');
    const applicable = getApplicableActions(actions, item);
    // Only files carry these; Companion answers `null` for what it does not know.
    const file = item.type === 'file' ? item.data : undefined;
    const size = file?.size;
    const mimeType = file?.mimeType;
    const modified = file?.modifiedDate;
    return (_jsxs(ModalDialog, { className: "uppy-ProviderDialog uppy-ItemDetail", "aria-labelledby": titleId, onDismiss: onClose, restoreFocus: (dialog) => {
            // The item's (re-rendered) row, or at least its list.
            const browser = dialog.closest('.uppy-ProviderBrowser');
            return (browser?.querySelector(`[data-uppy-item-id="${CSS.escape(item.id)}"]`) ?? browser?.querySelector('.uppy-ProviderBrowser-list'));
        }, children: [_jsxs("header", { className: "uppy-ItemDetail-header", children: [_jsx("h3", { id: titleId, className: "uppy-ItemDetail-name", children: name }), _jsx("button", { type: "button", className: "uppy-u-reset uppy-c-btn uppy-ItemDetail-close", "aria-label": i18n('close'), onClick: onClose, children: _jsx("svg", { "aria-hidden": "true", width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", children: _jsx("path", { d: "M2 2l10 10M12 2L2 12", stroke: "currentColor", "stroke-width": "1.6", "stroke-linecap": "round" }) }) })] }), _jsxs("div", { className: "uppy-ItemDetail-body", children: [_jsx("div", { className: "uppy-ItemDetail-preview", "data-testid": "file-detail-preview", children: previewUrl ? (_jsx("img", { src: previewUrl, alt: "", className: "uppy-ItemDetail-previewImage" })) : (_jsx("span", { className: "uppy-ItemDetail-previewIcon", "aria-hidden": "true", children: _jsx(ItemIcon, { itemIconString: item.data.icon }) })) }), _jsxs("dl", { className: "uppy-ItemDetail-facts", children: [typeof size === 'number' ? (_jsxs(_Fragment, { children: [_jsx("dt", { children: i18n('detailSize') }), _jsx("dd", { children: prettierBytes(size) })] })) : null, mimeType ? (_jsxs(_Fragment, { children: [_jsx("dt", { children: i18n('detailType') }), _jsx("dd", { children: mimeType })] })) : null, modified ? (_jsxs(_Fragment, { children: [_jsx("dt", { children: i18n('detailModified') }), _jsx("dd", { children: modified })] })) : null] })] }), _jsx("footer", { className: "uppy-ItemDetail-actions", children: applicable.map((action) => (_jsx("button", { type: "button", className: classNames('uppy-u-reset uppy-c-btn uppy-ItemDetail-action', action.danger && 'uppy-ItemDetail-action--danger'), onClick: () => {
                        onClose();
                        runAction(action, item);
                    }, children: action.label }, action.id))) })] }));
}
